/* Progressive enhancement: keep every original node and a readable no-JS fallback. */
if (!customElements.get('t3d-product-content')) {
  customElements.define('t3d-product-content', class extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.enhanced) {
        const content = this.querySelector('[data-t3d-content]');
        if (!content) return;
        this.organize(content, 0);
        content.querySelectorAll('img').forEach(image => {
          image.loading = 'lazy';
          image.decoding = 'async';
        });
        content.querySelectorAll('table').forEach((table, index) => {
          if (table.closest('.t3d-table-scroll')) return;
          const frame = document.createElement('div');
          frame.className = 't3d-table-scroll';
          frame.tabIndex = 0;
          frame.setAttribute('role', 'region');
          const heading = table.closest('.t3d-accordion')?.querySelector('summary');
          frame.setAttribute('aria-label', heading?.textContent.trim() || `Tabla del producto ${index + 1}`);
          table.before(frame);
          frame.append(table);
          if (Array.from(table.rows).some(row => Array.from(row.cells).reduce((sum, cell) => sum + cell.colSpan, 0) > 4)) table.classList.add('t3d-table--wide');
        });
        this.dataset.enhanced = 'true';
      }
      this.anchorListener ||= () => this.openAnchor();
      window.addEventListener('hashchange', this.anchorListener);
      this.openAnchor();
    }
    disconnectedCallback() { window.removeEventListener('hashchange', this.anchorListener); }
    hasContent(node) {
      return node.textContent.trim() || node.querySelector('img, picture, video, iframe, table, svg, audio');
    }
    accordion(heading, body, nested = false) {
      const details = document.createElement('details');
      details.className = `t3d-accordion${nested ? ' t3d-accordion--nested' : ''}`;
      const summary = document.createElement('summary');
      summary.append(heading);
      body.classList.add('t3d-accordion__body');
      details.append(summary, body);
      return details;
    }
    organize(container, depth) {
      const headings = Array.from(container.children).filter(node => /^H[2-6]$/.test(node.tagName));
      if (!headings.length) {
        this.gallery(container);
        if (depth === 0) this.compactIntro(container);
        return;
      }
      const level = Math.min(...headings.map(node => Number(node.tagName.slice(1))));
      const result = document.createDocumentFragment();
      let body = document.createElement('div');
      const overview = body;
      overview.className = 't3d-overview';
      result.append(overview);
      const groups = [];
      Array.from(container.childNodes).forEach(node => {
        if (node.nodeType === 1 && node.tagName === `H${level}`) {
          body = document.createElement('div');
          const details = this.accordion(node, body, depth > 0);
          result.append(details);
          groups.push({ details, heading: node, body });
        } else {
          body.append(node);
        }
      });
      container.append(result);
      if (!this.hasContent(overview)) overview.remove();
      else {
        this.gallery(overview);
        if (depth === 0) this.compactIntro(overview);
        else if (overview.querySelectorAll('img').length >= 4) {
          const galleryBody = document.createElement('div');
          galleryBody.append(...overview.childNodes);
          const heading = document.createElement(`h${level}`);
          heading.textContent = 'Ver características en imágenes';
          overview.append(this.accordion(heading, galleryBody, true));
        }
      }
      groups.forEach(({ details, heading, body }) => {
        // Standalone section labels (e.g. "Flujo estándar") must not become empty controls.
        if (!this.hasContent(body)) {
          const label = document.createElement('div');
          label.className = 't3d-section-label';
          label.append(heading, ...body.childNodes);
          details.replaceWith(label);
          return;
        }
        this.organize(body, depth + 1);
        this.splitStory(body);
      });
    }
    compactIntro(container) {
      const children = Array.from(container.children);
      if (!children.length) return;
      const first = children[0];
      const keepFirst = first.tagName === 'P' && !first.querySelector('img, video, iframe, table') && first.textContent.length < 1000;
      const remaining = children.slice(keepFirst ? 1 : 0);
      const long = remaining.some(node => node.querySelector('img, table, video, iframe') || node.matches('img, table, video, iframe')) || remaining.reduce((n, node) => n + node.textContent.length, 0) > 500 || remaining.some(node => node.matches('ul, ol') && node.children.length > 4);
      if (!long) return;
      const body = document.createElement('div');
      // Move nodes, including whitespace and links; never truncate or replace source content.
      const nodes = Array.from(container.childNodes);
      const start = keepFirst ? nodes.indexOf(first) + 1 : 0;
      nodes.slice(start).forEach(node => body.append(node));
      const heading = document.createElement('h3');
      heading.textContent = remaining.length === 1 && remaining[0].matches('ul, ol') ? 'Características principales' : 'Descripción completa';
      container.append(this.accordion(heading, body));
    }
    isImage(node) {
      return node && (node.tagName === 'IMG' || (node.querySelectorAll('img').length === 1 && !node.textContent.trim() && !node.querySelector('video, iframe, table')));
    }
    gallery(container) {
      const nodes = Array.from(container.children);
      let index = 0;
      while (index < nodes.length) {
        const cards = [];
        let end = index;
        while (end < nodes.length) {
          const node = nodes[end];
          if (node.tagName === 'P' && node.querySelector('strong, b') && node.textContent.length < 450 && !node.querySelector('img') && this.isImage(nodes[end + 1])) {
            cards.push([node, nodes[end + 1]]);
            end += 2;
          } else if (this.isImage(node)) {
            cards.push([node]);
            end += 1;
          } else break;
        }
        if (cards.length >= 2) {
          const grid = document.createElement('div');
          grid.className = 't3d-feature-grid';
          nodes[index].before(grid);
          cards.forEach(parts => {
            const card = document.createElement('div');
            card.className = 't3d-feature-card';
            card.append(...parts);
            grid.append(card);
          });
        }
        index = Math.max(end, index + 1);
      }
    }
    splitStory(body) {
      if (body.querySelector('h2, h3, h4, h5, h6, table, details, iframe, video, .t3d-feature-grid')) return;
      const images = body.querySelectorAll('img');
      if (images.length !== 1 || !body.textContent.trim()) return;
      let media = images[0];
      while (media.parentElement !== body && !media.parentElement.textContent.trim() && media.parentElement.querySelectorAll('img').length === 1) media = media.parentElement;
      if (media.parentElement !== body) return;
      const text = document.createElement('div');
      text.className = 't3d-story__text';
      Array.from(body.childNodes).filter(node => node !== media).forEach(node => text.append(node));
      const figure = document.createElement('div');
      figure.className = 't3d-story__media';
      figure.append(media);
      body.classList.add('t3d-accordion__body--split');
      body.append(text, figure);
    }
    openAnchor() {
      let id;
      try { id = decodeURIComponent(window.location.hash.slice(1)); } catch { return; }
      if (!id) return;
      const target = document.getElementById(id);
      if (!target || !this.contains(target)) return;
      let parent = target.closest('details');
      let opened = false;
      while (parent && this.contains(parent)) {
        if (!parent.open) { parent.open = true; opened = true; }
        parent = parent.parentElement?.closest('details');
      }
      if (opened) requestAnimationFrame(() => target.scrollIntoView({ block: 'start' }));
    }
  });
}
